import React, { useEffect, useContext } from 'react';
import useUser from '../hooks/useUser.js';
import UserContext, { loadUserInfo } from '../contexts/user.js';
import GlobalContext from '../contexts/global.js';
import StateContext from '../contexts/state.js';
import losAlias from '../helpers/losAlias.js';
import { getQueryOverrideList } from '../helpers/queryOverride.js';
import { getUserOverride } from '../helpers/api/getUserData.js';

/* Override snippet values are config keys, either a single string or a list */
const toKeyList = value => [ value ].flat().filter(Boolean);

/* Override values are added to the SSO ones, or replace them when the mode is "replace" */
const applyOverride = ({ ssoKeys, overrideKeys, mode }) => (
    overrideKeys.length && mode === "replace" ? overrideKeys : [ ...ssoKeys, ...overrideKeys ]
);

/* Matching config options, in config order and without duplicates */
const matchOptions = (options, keys) => options.filter(({ key }) => keys.includes(key));

const UserConfig = () => {
    const { userContext, setUserContext } = useContext(UserContext);
    const { globalContext } = useContext(GlobalContext);
    const { updateState } = useContext(StateContext);
    const { logout } = useUser();

    useEffect(() => {
        let isMounted = true;
        userContext.authenticated && (async () => {
            const [ response, override ] = await Promise.all([
                loadUserInfo({ logout }),
                getUserOverride()
            ]);
            if(isMounted && response) {
                setUserContext({
                    ...userContext,
                    user: response,
                    override
                });
            }
        })();
        return () => {
          isMounted = false;
        };
      }, [userContext.authenticated]);

    /* Preselect LoS from the query, override snippet and SSO profile once both the config options and user data are available */
    useEffect(() => {
        if(!globalContext?.los?.length) {
            return;
        }

        const toLosKey = value => {
            const losValue = (value ?? "").toLowerCase().trim();
            return losAlias[losValue] ?? losValue;
        };

        /* Process branching before going through aliases */
        let ssoLosValue = (userContext.user?.LOS ?? userContext.user?.businessunit ?? "").toLowerCase().trim();

        /* If LOS is "Advisory", then use the "LocalLoS1" instead and expect "Deals" or "Consulting" */
        if(ssoLosValue === "advisory") {
            ssoLosValue = userContext.user?.pwcLocalLOSLevel1;
        }

        const losKeys = getQueryOverrideList("los")?.map(toLosKey) ?? applyOverride({
            ssoKeys: toKeyList(toLosKey(ssoLosValue)),
            overrideKeys: toKeyList(userContext.override?.los),
            mode: userContext.override?.losMode
        });
        const matchedLos = matchOptions(globalContext.los, losKeys);

        updateState({
            currentLos: matchedLos.length ? matchedLos : [ globalContext.los[0] ],
            userLos: matchedLos
        });
    }, [globalContext?.los, userContext?.user, userContext?.override]);

    /* Preselect Locations from the query, override snippet and SSO profile once both the config options and user data are available */
    useEffect(() => {
        if(!globalContext?.locations?.length) {
            return;
        }

        const locationKeys = getQueryOverrideList("location")?.map(value => value.toLowerCase()) ?? applyOverride({
            ssoKeys: toKeyList(userContext.user?.country?.toLowerCase()),
            overrideKeys: toKeyList(userContext.override?.location),
            mode: userContext.override?.locationMode
        });
        const matchedLocations = matchOptions(globalContext.locations, locationKeys);

        updateState({
            currentLocation: matchedLocations,
            userLocation: matchedLocations
        });
    }, [globalContext?.locations, userContext?.user, userContext?.override]);

    return null;
};

export default UserConfig;
